import {EditorState} from "prosemirror-state";
import type {EditorView} from "prosemirror-view";

import {schema} from '../../../../../Editor/module/config/schema';
import {isTextSelection} from '../../../../../Editor/utils/isTextSelection';

import {LinkInContextEditBarProps} from "./LinkInContextEditBar";
import type {AppState} from "@stores/AppState";

export interface OpenLinkInContextEditBarParams {
    isEditingAtOpen: boolean;
    defaultValue?: string;
}

export const openLinkInContextEditBarBySelection = (view: EditorView, state: EditorState, params: OpenLinkInContextEditBarParams) => {
    if (!isTextSelection(state.selection)) {
        return false;
    }
    const appState = view.state.schema.cached.app.appState as AppState;
    const {empty, $from, $to, $cursor} = state.selection;
    if (!empty) {
        openLinkInContextEditBar(
            appState,
            `${$from.pos}-${$to.pos}`,
            () => {
                const coordsAtStart = view.coordsAtPos($from.pos);
                const coordsAtEnd = view.coordsAtPos($to.pos);
                return {
                    height: coordsAtStart.bottom - coordsAtStart.top,
                    width: coordsAtEnd.right - coordsAtStart.left,
                    x: coordsAtStart.left,
                    y: coordsAtStart.top,
                } as DOMRect
            },
            params
        );
        return true;
    } else if ($cursor) {
        const nodePos = $cursor.pos;
        const node = state.doc.nodeAt(nodePos); /* the text node with link mark */
        if (node) {
            const linkMark = node.marks.find(mark => mark.type === schema.marks.link);
            if (linkMark === undefined) {
                return false;
            }

            openLinkInContextEditBar(
                appState,
                `pos-${nodePos}`,
                () => {
                    const coordsAtCursor = view.coordsAtPos(nodePos);
                    return {
                        height: 0,
                        width: 0,
                        x: coordsAtCursor.left,
                        y: coordsAtCursor.top,
                    } as DOMRect
                },
                {
                    ...params,
                    defaultValue: linkMark?.attrs.href as string | undefined,
                    nodePos: nodePos,
                }
            );
            return true;
        }
    }
    return false;
}

export const openLinkInContextEditBar = (
    appState: AppState,
    id: string,
    getTargetBoundingBox: () => DOMRect,
    params: Partial<LinkInContextEditBarProps>
) => {
    appState.addAdditionalContextMenu({
        type: 'editor-link',
        id: id,
        isDark: true,
        menuPlacement: 'block-start',
        menuAlignment: 'center',
        VERTICAL_SHIFT: 10,
        withPointer: true,
        getTargetBoundingBox: getTargetBoundingBox,
        minWidth: 340,
        params: {
            isEditingAtOpen: false,
            ...params,
        }
    });
}
